// Textbausteine mit CRM-Feldern (Phase 2.A): `/` im Editor öffnet sie. Die Texte folgen dem Bob-Handbuch
// (Kapitel produkte-preise, aufnahmetag-ablauf, vorbereitung-schule, allin-wechsel, logistik); Preise werden
// nicht geraten, sondern aus Einrichtungsart und Kinderzahl nach der Handbuch-Staffel berechnet.
// Anrede: die lesende Person wird geduzt (du), die Schule als Ganzes mit ihr/euch (Till, 22.10.2026). Fehlt ein
// Wert, bleibt eine [PRÜFEN: …]-Markierung stehen, damit der Senden-Knopf warnt.
import type { MailKontext } from './crm.ts';

export type Baustein = {
  key: string; // /ablauf
  titel: string;
  gruppe: 'Minimusikertag' | 'Preise' | 'Schulsong' | 'Organisation';
  text: string; // Absätze durch Leerzeile, Platzhalter in {geschweiften Klammern}
  kapitel?: string; // Handbuch-Kapitel als Quelle
};

export const PLATZHALTER = [
  'schule',
  'ansprechperson',
  'vorname',
  'ort',
  'termin',
  'modell',
  'kinder',
  'allin_preis',
  'allin_preis_rabatt',
  'allin_staffel',
  'allin_fenster',
  'buchungslink',
  'minimusikerin',
] as const;
export type Platzhalter = (typeof PLATZHALTER)[number];

export const BAUSTEINE: Baustein[] = [
  {
    key: 'ablauf',
    titel: 'Ablauf am Aufnahmetag',
    gruppe: 'Minimusikertag',
    kapitel: 'aufnahmetag-ablauf',
    text: `So läuft der Minimusikertag bei euch ab: Unsere Minimusiker:in kommt gegen 7:30 Uhr, der Aufbau dauert höchstens eine Viertelstunde – gebraucht werden nur ein kleiner Tisch und ein Stromanschluss.

Zum Start kommen alle Kinder zusammen (Turnhalle, Aula oder Foyer): Warm-up und ein gemeinsames Lied, etwa 30–45 Minuten. Danach ziehen wir in einen kleineren Raum (Klassen- oder Musikraum), und die Klassen nehmen nacheinander ihren Beitrag auf – je etwa 15–20 Minuten. Jedes Kind bekommt anschließend eine Urkunde und seine minicard. Mittags ist der Tag geschafft.

Den genauen Zeitplan stimmst du direkt mit eurer Minimusiker:in ab, der Kontakt steht im Pädagogen-Portal.`,
  },
  {
    key: 'vorbereitung',
    titel: 'Vorbereitung & Liedauswahl',
    gruppe: 'Minimusikertag',
    kapitel: 'vorbereitung-schule',
    text: `Die Vorbereitung ist einfach: Ab 8 Wochen vor dem Termin schalten wir jede Woche ein kurzes Vorbereitungs-Video im Login-Bereich frei (Liedauswahl, Playbacks, Warm-ups, Zeitplanung, Elterninfo …).

Die Lieder wählt ihr selbst – am besten Lieder, die die Kinder schon kennen oder sich gut einüben lassen. Eine Liste bewährter Lieder mit unseren Playbacks (mit und ohne Gesang) bekommst du dazu. Die Hauptlehrkraft trägt die Lieder im Pädagogen-Portal ein und spricht die finale Liste spätestens 2 Wochen vorher mit eurer Minimusiker:in ab.

Wichtig wegen des Tonträgers: bitte keine fremden Playbacks (YouTube, Spotify) – zum Üben und bei der Aufnahme nur unsere.`,
  },
  {
    key: 'preise-klassisch',
    titel: 'Preise klassisch',
    gruppe: 'Preise',
    kapitel: 'produkte-preise',
    text: `Beim klassischen Modell zahlt die Einrichtung für den Tag selbst nichts. Die Familien entscheiden im Eltern-Portal, was sie möchten (Freischalten der Aufnahmen, CD, Kinderliederbox). Nur bei kleinen Einrichtungen fällt eine Projektdurchführungs-Pauschale von 600 € an, und bei Anfahrten über 100 km eine Anfahrtspauschale von 200 € (alle Preise brutto).`,
  },
  {
    key: 'preise-allin',
    titel: 'Preise ALL-IN',
    gruppe: 'Preise',
    kapitel: 'produkte-preise',
    text: `Beim ALL-IN-Modell zahlt die Einrichtung einen Fixpreis, und für die Familien ist alles inklusive – die Aufnahmen sind für alle frei. Für {schule} ({allin_staffel}) wären das {allin_preis} brutto. Viele Schulen lassen den Betrag über den Förderverein oder den Schulträger laufen.`,
  },
  {
    key: 'allin-wechsel',
    titel: 'Wechsel auf ALL-IN',
    gruppe: 'Preise',
    kapitel: 'allin-wechsel',
    text: `Du kannst auch nachträglich von klassisch auf ALL-IN wechseln: Fünf Wochen vor dem Termin bekommt die Hauptlehrkraft dazu eine Mail mit einem Wechselfenster von vier Tagen, in dem sie im Pädagogen-Portal selbst umstellen kann. Beim Wechsel gibt es 10 % Nachlass auf die Pauschale – für {schule} also {allin_preis_rabatt} statt {allin_preis}. Für euren Termin am {termin} läuft das Fenster bis {allin_fenster}.`,
  },
  {
    key: 'buchungslink',
    titel: 'Buchungslink',
    gruppe: 'Organisation',
    kapitel: 'buchung-ablauf',
    text: `Den Termin buchst du am einfachsten direkt online – dort siehst du alle freien Tage und wählst, was euch passt:
{buchungslink}

Der früheste buchbare Tag steht dort direkt im Kalender. Nach der Buchung bekommst du sofort eine Bestätigung mit allen weiteren Schritten.`,
  },
  {
    key: 'anfahrt',
    titel: 'Anfahrt & Region',
    gruppe: 'Organisation',
    kapitel: 'produkte-preise',
    text: `Wir kommen mit unserem mobilen Tonstudio zu euch nach {ort}. [PRÜFEN: Region/Minimusiker:in für diese Gegend bestätigen] Bei Anfahrten über 100 km fällt eine Anfahrtspauschale von 200 € an.`,
  },
  {
    key: 'logistik',
    titel: 'Flyer & Mitmachpaket',
    gruppe: 'Organisation',
    kapitel: 'logistik',
    text: `Etwa zwei Wochen vor dem Termin kommt das Mitmachpaket mit Poster und Elternflyern bei euch an – die Flyer sind schon mit eurem Einrichtungsnamen, QR-Code und dem Link fürs Eltern-Portal bedruckt. Du musst nichts vorbereiten, außer die Flyer an die Kinder zu verteilen.`,
  },
  {
    key: 'schulsong',
    titel: 'Schulsong (Preise)',
    gruppe: 'Schulsong',
    kapitel: 'produkte-preise',
    text: `Beim Schulsong schreiben wir ein eigenes Lied für eure Schule: als Exklusiv-Komposition für 2.200 € oder auf Vorlage für 1.500 €. Die Aufnahme mit den Kindern kostet 500 € als eigener Termin, 250 € am klassischen Minimusikertag und ist am ALL-IN-Tag inklusive. Optional: Noten (100 €), Übematerial (100 €) und Streaming-Veröffentlichung (89 €). Alle Preise brutto.`,
  },
  {
    key: 'mix',
    titel: 'Nach dem Tag: Mix & Eltern-Portal',
    gruppe: 'Minimusikertag',
    kapitel: 'aufnahmetag-ablauf',
    text: `Nach dem Aufnahmetag gehen die Aufnahmen ins Studio; der fertige Mix ist spätestens fünf Tage später da. Dann gibt es Hörproben im Eltern-Portal, und die Familien bekommen eine Mail, sobald alles bereit ist.`,
  },
  // Aus der Auswertung der gesendeten Antworten (prompts/analyse-gesendet-2026-10.md): Formulierungen, die das
  // Büro tatsächlich benutzt, als Bausteine für die häufigsten Anliegen.
  {
    key: 'tracklist',
    titel: 'Tracklist freigeben (Produktion)',
    gruppe: 'Organisation',
    text: `Es wäre wichtig, dass ihr im Portal einmal die finale Reihenfolge der Songs (Tracklist) bestätigt, damit wir die Produkte herstellen können. Alle Produkte werden erst produziert, wenn die Tracklist freigegeben ist – danach geht es mit Produktion und Versand los, das dauert etwa ein bis zwei Wochen.`,
  },
  {
    key: 'login',
    titel: 'Login ohne Passwort',
    gruppe: 'Organisation',
    text: `Der Login ist ganz einfach: Auf der Login-Seite die eigene E-Mail-Adresse eingeben, dann kommt sofort ein frischer Login-Link – ein Passwort gibt es nicht. Falls keine Mail ankommt, lohnt ein Blick in den Spam-Ordner.`,
  },
  {
    key: 'nachfassen',
    titel: 'Nachfassen bei Funkstille',
    gruppe: 'Organisation',
    text: `ich wollte einmal nachhören, ob das Thema bei euch an der {schule} noch aktuell ist. Gibt es etwas, womit wir euch unterstützen können? Andernfalls melden wir uns einfach in ein paar Monaten noch einmal.`,
  },
];

// ALL-IN-Staffel nach Handbuch (produkte-preise): Schulen nach Zügigkeit (Kinderzahl), KiTas nach Kinderzahl.
export function allinStaffel(art: string, kinder: number | null): { stufe: string; preis: number } {
  const kita = /kita|kindergarten|kinderta/i.test(art);
  if (kita) {
    if (kinder == null) return { stufe: 'KiTa bis 50 Kinder', preis: 849 };
    if (kinder <= 50) return { stufe: 'KiTa bis 50 Kinder', preis: 849 };
    if (kinder <= 75) return { stufe: 'KiTa 51–75 Kinder', preis: 1199 };
    return { stufe: 'KiTa ab 76 Kinder', preis: 1499 };
  }
  if (kinder == null) return { stufe: 'zweizügig, bis 249 Kinder', preis: 2499 };
  if (kinder <= 149) return { stufe: 'einzügig, bis 149 Kinder', preis: 1499 };
  if (kinder <= 249) return { stufe: 'zweizügig, bis 249 Kinder', preis: 2499 };
  if (kinder <= 349) return { stufe: 'dreizügig, bis 349 Kinder', preis: 3499 };
  return { stufe: 'vierzügig und mehr', preis: 4499 };
}

// Ganze Beträge ohne Nachkommastellen, krumme (z. B. 10 % Nachlass: 2.249,10 €) mit zwei – wie im Handbuch
export const euro = (n: number) =>
  n.toLocaleString('de-DE', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }) + ' €';

// Self-Booking-Link wie CRM rules-lib.js selfBookingUrl(): KiTa/Schule-Seite, vorbefüllt mit Kontakt und Einrichtung.
const KITA = /kita|kindergarten|kinderta|krippe|kindertagesst/i;
const BUCHEN_KITA = 'https://www.minimusiker.de/minimusikertag-kita-buchen.html';
const BUCHEN_SCHULE = 'https://www.minimusiker.de/minimusikertag-buchen.html';

// Ohne Einrichtung im CRM (Absender unbekannt, z. B. Elternbeirat über Gmail) gibt es den Link ohne
// Vorbefüllung; `hinweis` ist der Verlauf des Threads und entscheidet zwischen KiTa- und Schul-Seite.
// Ohne Verlauf (Verfassen ohne Thread) bleibt es bei null → [PRÜFEN].
export function buchungslink(
  k: MailKontext | null | undefined,
  hinweis?: string,
): string | null {
  const e = k?.einrichtung;
  if (!e) return hinweis === undefined ? null : KITA.test(hinweis) ? BUCHEN_KITA : BUCHEN_SCHULE;
  const base = KITA.test(e.art) ? BUCHEN_KITA : BUCHEN_SCHULE;
  const p = new URLSearchParams();
  if (k?.kontakt?.email) p.set('email', k.kontakt.email);
  p.set('paket', (k?.lead?.interesse ?? []).some((x) => /all-?in/i.test(x)) ? 'all-in' : 'basic');
  if (e.name) p.set('schule', e.name);
  if (e.plz) p.set('plz', e.plz);
  if (e.ort) p.set('ort', e.ort);
  if (k?.kontakt?.name) p.set('name', k.kontakt.name);
  return `${base}?${p.toString()}`;
}

const fmtDatum = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Berlin',
      });
};

// Platzhalterwerte aus dem CRM-Kontext (null = unbekannt → [PRÜFEN])
export function platzhalterWerte(
  k: MailKontext | null | undefined,
  hinweis?: string,
): Partial<Record<Platzhalter, string>> {
  const out: Partial<Record<Platzhalter, string>> = {};
  const e = k?.einrichtung;
  if (e?.name) out.schule = e.name;
  if (e?.ort) out.ort = e.ort;
  if (e?.kinder != null) out.kinder = String(e.kinder);
  if (k?.kontakt?.name) {
    out.ansprechperson = k.kontakt.name;
    const vn = k.kontakt.name.split(/\s+/)[0];
    if (vn) out.vorname = vn;
  }
  const heute = new Date().toISOString().slice(0, 10);
  const termin =
    (k?.termine ?? [])
      .filter((t) => t.datum >= heute && !/storn|abgesagt/i.test(t.status))
      .sort((a, b) => (a.datum < b.datum ? -1 : 1))[0] ?? null;
  if (termin) {
    const d = fmtDatum(termin.datum);
    if (d) out.termin = d;
    if (termin.modell) out.modell = termin.modell;
    if (termin.staff) out.minimusikerin = termin.staff;
    const f = fmtDatum(termin.allinFensterBis);
    if (f) out.allin_fenster = f;
  }
  if (e) {
    const s = allinStaffel(e.art, termin?.kinderGeschaetzt ?? e.kinder ?? null);
    out.allin_staffel = s.stufe;
    out.allin_preis = euro(s.preis);
    out.allin_preis_rabatt = euro(Math.round(s.preis * 0.9 * 100) / 100);
  }
  const link = buchungslink(k, hinweis);
  if (link) out.buchungslink = link;
  return out;
}

const LABEL: Record<Platzhalter, string> = {
  schule: 'Name der Einrichtung',
  ansprechperson: 'Name der Ansprechperson',
  vorname: 'Vorname',
  ort: 'Ort',
  termin: 'Termindatum',
  modell: 'Modell (ALL-IN/klassisch)',
  kinder: 'Kinderzahl',
  allin_preis: 'ALL-IN-Preis',
  allin_preis_rabatt: 'ALL-IN-Preis mit 10 %',
  allin_staffel: 'ALL-IN-Staffel',
  allin_fenster: 'Ende des Wechselfensters',
  buchungslink: 'Buchungslink',
  minimusikerin: 'zuständige Minimusiker:in',
};

export function fuelleBaustein(text: string, werte: Partial<Record<string, string>>): string {
  return text.replace(/\{([a-z_]+)\}/g, (_, key: string) => {
    const v = werte[key];
    if (v) return v;
    const label = (LABEL as Record<string, string>)[key] ?? key;
    return `[PRÜFEN: ${label}]`;
  });
}

// Text → HTML für den Editor (Absätze, Zeilenumbrüche, Links)
export function bausteinHtml(text: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text
    .split(/\n{2,}/)
    .map(
      (p) =>
        '<p>' +
        esc(p)
          .replace(/(https?:\/\/[^\s<]+)/g, (u) => `<a href="${u}">${u}</a>`)
          .replace(/\n/g, '<br>') +
        '</p>',
    )
    .join('');
}
